import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import moment, { Moment } from 'moment';
import { ui } from '@/constants';
import { OrderApiException, OrderErrorCode, OrderWithContext, PostSubmitCancelOrderRequestParams } from '@/models';
import { Routes } from '@/router';
import { ErrorEvent } from '@/services/analytics/events';
import { CancelOrderVisibleEvent } from '@/services/analytics/events/cancel-order-visible-event';
import { CancelOrderConfirmationVisibleEvent } from '@/services/analytics/events/cancel-order-confirmation-visible-event';

@Component({
  name: 'CmgOrderConfirmationCancelOrderModal',
})
export default class CmgOrderConfirmationCancelOrderModal extends Vue {
  selectedCancellationReason: string = '';
  validationErrorMessage: string = '';
  currentTime: Moment = moment();
  updateCurrentTimeInterval: NodeJS.Timeout | null = null;

  @Watch('visible', { immediate: true })
  onVisibleChange(newVal: boolean) {
    // when opened or closed, reset modal state
    this.validationErrorMessage = '';
    this.selectedCancellationReason = '';

    // if the modal closes, stop the timer that updates the current time value 
    if (!newVal && this.updateCurrentTimeInterval !== null) {
      clearInterval(this.updateCurrentTimeInterval);
    } else if (newVal) {
      // if the modal is being opened, we need to start tracking and updating the current time
      this.updateCurrentTimeInterval = setInterval(this.updateCurrentTime, 100);

      // we also need to send an analytics event that the user has opened the cancel order modal
      this.$services.analytics.send(new CancelOrderVisibleEvent());
    }
  }

  @Watch('timeLeftInSeconds', { immediate: true })
  onTimeLeftInSecondsChange(newVal: number, oldVal: number) {
    if (newVal === 0 && oldVal > 0) {
      this.close();
      this.displayCancelOrderFailureModal(OrderErrorCode.OrderNotEligibleForCancellation);
    }
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.postSubmitCancelOrder.visible;
  }

  get radioButtonOptions(): Array<string> {
    return [
      'Wrong pickup location',
      'Wrong pickup time',
      'Wrong menu item chosen',
      'I\'d rather get delivery',
      'Other'
    ]
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get displayValidationErrorMessage(): boolean {
    return this.validationErrorMessage !== '';
  }

  get displayTimerCountdown(): boolean {
    return this.timeLeftInSeconds > 0;
  }

  // calculates time left for user to cancel order in seconds
  get timeLeftInSeconds(): number {
    if (this.order && typeof this.order.order.orderCancellationCutoffUtc === 'string') {
      const cutOffTime: Moment = moment(this.order.order.orderCancellationCutoffUtc)
      const differenceInSeconds: number = cutOffTime.diff(this.currentTime, 'seconds')

      return differenceInSeconds;
    } else {
      return -1;
    }
  }

  get timeLeftDisplay(): string {
    return this.timeLeftInSeconds >= 0 ? `${this.timeLeftInSeconds}s` : '0s';
  }

  get order(): OrderWithContext | null {
    return this.$appStore.state.order.lastOrder.order;
  }

  onSelectCancellationReason(text: string): void {
    this.validationErrorMessage = '';
    this.selectedCancellationReason = text;
  }

  isCancellationReasonSelected(text: string): boolean {
    return text === this.selectedCancellationReason;
  }

  close(): void {
    this.$services.ui.setModalVisibility('postSubmitCancelOrder', false);
    this.$services.ui.focusBack();
  }

  displaySuccessModal(): void {
    this.$services.analytics.send(new CancelOrderConfirmationVisibleEvent(this.selectedCancellationReason));
    this.close();
    this.$services.ui.setModalVisibility('postSubmitCancelOrderSuccess', true);
    this.$services.order.setLastOrder(null, null);
    this.$router.push({ name: Routes.Home });
  }

  async onCancelOrderClick(): Promise<void> {
    // first need to validate that the user has selected a cancellation reason
    const isValid = this.validate();

    if (isValid && this.order?.order.orderId) {
      // create cancel order request
      const requestParams: PostSubmitCancelOrderRequestParams = {
        cancellationReason: this.selectedCancellationReason,
      }

      try {
        // send cancel order request
        await this.$services.order.postSubmitCancelOrder(this.order, requestParams);

        // if it made it this far, it was successful
        this.displaySuccessModal();
      } catch (e: any) {
        await this.handlePostSubmitCancelOrderError(e);
      }
    }
  }

  private async handlePostSubmitCancelOrderError(e: any): Promise<void> {
    this.close();
    this.$services.logger.error('error cancelling order', e);

    if ( // the order was no longer within the cancellation window
      e instanceof OrderApiException
      &&
      e.errors.findIndex(e => e.errorCode === OrderErrorCode.OrderNotEligibleForCancellation) > -1
    ) {
      // open the cancellation failure modal and display outside cancellation reason
      await this.displayCancelOrderFailureModal(OrderErrorCode.OrderNotEligibleForCancellation);
    } else { // there was another error not related to being outside of the cancellation window
      // open the cancellation with basic technical support / talk to pepper error
      await this.displayCancelOrderFailureModal();
    }
  }

  private async displayCancelOrderFailureModal(errorCode: string = ''): Promise<void> {
    // if errorcode is passed, it will display "outside of cancellation window" error.
    // otherwise, it will display a generic "technical issues / chat with pepper" error.
    await this.$services.ui
      .setModalVisibility(
        'postSubmitCancelOrderFailure',
        true,
        { errorCode }
      );

    if (errorCode === OrderErrorCode.OrderNotEligibleForCancellation) {
      // send analytics event with reason being outside of cancellation window
      this.sendErrorAnalyticsEvent(
        'We’re no longer able to cancel your order because the cancellation period has closed.'
      )
    } else {
      // send analytics event with unknown technical issue being the reason
      this.sendErrorAnalyticsEvent(
        'Due to technical issues, we’re no longer able to cancel your order. Please chat with Pepper at this time'
      )
    }
  }

  private sendErrorAnalyticsEvent(errorDescription: string) {
    this.$services.analytics.send(
      new ErrorEvent(
        'order',
        errorDescription,
        'cancel-order-modal'
      )
    );
  }

  private validate(): boolean {
    // check if radio button was selected
    if (this.selectedCancellationReason === '') {
      this.validationErrorMessage = 'Selection Required';
      return false;
    }

    return true;
  }

  private updateCurrentTime(): void {
    this.currentTime = moment();
  }
}
